import { useState, useEffect, useCallback } from 'react';
import { MapPin, Plus, Edit2, Trash2, X, Check, RefreshCw, Users } from 'lucide-react';
import { useSortable, SortTh } from '../hooks/useSortable';
import { listSites, createSite, updateSite, deleteSite, listVoiceRoutingPolicies } from '../services/portalApi';

function Flash({ msg, onDismiss }) {
  if (!msg) return null;
  const isErr = msg.type === 'error';
  return (
    <div style={{
      display: 'flex', alignItems: 'center', gap: 8, padding: '10px 16px',
      borderRadius: 8, marginBottom: 16, fontSize: 13,
      background: isErr ? 'var(--err-bg)' : 'var(--ok-bg)',
      border: `1px solid ${isErr ? 'var(--err-btn-bg)' : 'var(--ok-bg)'}`,
      color: isErr ? 'var(--err)' : 'var(--ok)',
    }}>
      <span style={{ flex: 1 }}>{msg.text}</span>
      <button onClick={onDismiss} style={{ background: 'none', border: 'none', cursor: 'pointer', color: 'inherit', padding: 2, display: 'flex' }}>
        <X size={13}/>
      </button>
    </div>
  );
}

function SiteModal({ site, onClose, onSave, customers = [], isAdmin = false }) {
  const [name,         setName]         = useState(site?.name || '');
  const [customerId,   setCustomerId]   = useState(site?.customer_id ? String(site.customer_id) : '');
  const [vrpName,      setVrpName]      = useState(site?.vrp_name || '');
  const [alertOnBusy,  setAlertOnBusy]  = useState(Boolean(site?.alert_on_busy));
  const [vrps,         setVrps]         = useState([]);
  const [saving,       setSaving]       = useState(false);
  const [err,          setErr]          = useState('');

  // Resolve effective customer id for VRP lookup
  const effectiveCid = customerId ? Number(customerId) : (site?.customer_id || null);

  useEffect(() => {
    if (!effectiveCid) { setVrps([]); return; }
    listVoiceRoutingPolicies(effectiveCid)
      .then(d => setVrps(d.policies || []))
      .catch(() => setVrps([]));
  }, [effectiveCid]);

  const handleSave = async () => {
    if (!name.trim()) { setErr('Name is required'); return; }
    setSaving(true);
    setErr('');
    try {
      const cid = isAdmin ? (customerId ? Number(customerId) : null) : undefined;
      await onSave(name.trim(), cid, vrpName || null, alertOnBusy);
    } catch (e) {
      setErr(e.message);
      setSaving(false);
    }
  };

  return (
    <div className="modal-overlay" onClick={onClose}>
      <div className="modal" onClick={e => e.stopPropagation()}>
        <div className="modal-header">
          <div className="modal-title">{site ? 'Edit Site' : 'New Site'}</div>
          <button className="btn btn-secondary btn-xs" onClick={onClose}><X size={12}/></button>
        </div>
        <div className="modal-body">
          <div className="form-group">
            <label className="form-label">Site Name</label>
            <input
              className="form-input"
              placeholder="e.g. London HQ, Manchester Branch"
              value={name}
              onChange={e => { setName(e.target.value); setErr(''); }}
              onKeyDown={e => e.key === 'Enter' && handleSave()}
              autoFocus
            />
            {err && <div style={{ fontSize: 12, color: 'var(--err)', marginTop: 4 }}>{err}</div>}
          </div>
          {isAdmin && (
            <div className="form-group">
              <label className="form-label" style={{ display: 'flex', alignItems: 'center', gap: 6 }}>
                <Users size={12} color="var(--muted)"/> Assign to Customer
              </label>
              <select
                className="form-input select-input"
                value={customerId}
                onChange={e => { setCustomerId(e.target.value); setVrpName(''); }}
              >
                <option value="">— No customer (unassigned) —</option>
                {customers.map(c => <option key={c.id} value={c.id}>{c.name}</option>)}
              </select>
              <div className="form-hint">Sites without a customer are visible to admins only</div>
            </div>
          )}
          <div className="form-group">
            <label className="form-label">Voice Routing Policy (Teams)</label>
            <select
              className="form-input select-input"
              value={vrpName}
              onChange={e => setVrpName(e.target.value)}
              disabled={!effectiveCid}
            >
              <option value="">— Inherit from customer default —</option>
              {vrps.map(v => (
                <option key={v.id} value={v.name}>{v.name}{v.description ? ` — ${v.description}` : ''}</option>
              ))}
            </select>
            <div className="form-hint">
              {effectiveCid
                ? vrps.length === 0
                  ? 'No policies synced for this customer — sync via the Teams connector first'
                  : 'Overrides the customer default VRP when assigning Teams numbers for DIDs at this site'
                : 'Assign this site to a customer to select a VRP'
              }
            </div>
          </div>

          <div className="form-group" style={{ marginBottom: 0 }}>
            <label className="form-label">Alert on Busy Calls</label>
            <div style={{ display: 'flex', alignItems: 'center', gap: 12, marginTop: 6 }}>
              <button
                type="button"
                onClick={() => setAlertOnBusy(v => !v)}
                style={{
                  width: 42, height: 24, borderRadius: 12, border: 'none', cursor: 'pointer',
                  background: alertOnBusy ? 'var(--ok)' : 'var(--muted-3)',
                  transition: 'background 0.2s', position: 'relative', padding: 0, flexShrink: 0,
                }}
                aria-checked={alertOnBusy}
                role="switch"
              >
                <span style={{
                  position: 'absolute', top: 3,
                  left: alertOnBusy ? 21 : 3,
                  width: 18, height: 18, borderRadius: '50%', background: 'var(--on-brand)',
                  transition: 'left 0.2s', display: 'block',
                }}/>
              </button>
              <span style={{ fontSize: 13, color: alertOnBusy ? 'var(--ok)' : 'var(--muted-2)' }}>
                {alertOnBusy ? 'Busy calls will trigger alerts for this site' : 'Busy calls excluded from alerts (default)'}
              </span>
            </div>
            <div className="form-hint" style={{ marginTop: 6 }}>
              By default busy calls are not reported as failures. Enable to include them in the Alerts tab for numbers at this site.
            </div>
          </div>
        </div>
        <div className="modal-footer">
          <button className="btn btn-secondary" onClick={onClose}>Cancel</button>
          <button className="btn btn-primary" onClick={handleSave} disabled={saving}>
            {saving ? <RefreshCw size={12} style={{ animation: 'spin 0.8s linear infinite' }}/> : <Check size={12}/>}
            {site ? 'Save Changes' : 'Create Site'}
          </button>
        </div>
      </div>
    </div>
  );
}

function DeleteModal({ site, onClose, onConfirm }) {
  const [deleting, setDeleting] = useState(false);

  const handleConfirm = async () => {
    setDeleting(true);
    try { await onConfirm(); }
    catch { setDeleting(false); }
  };

  return (
    <div className="modal-overlay" onClick={onClose}>
      <div className="modal" style={{ maxWidth: 420 }} onClick={e => e.stopPropagation()}>
        <div className="modal-header">
          <div className="modal-title" style={{ color: 'var(--err)' }}>Delete Site</div>
          <button className="btn btn-secondary btn-xs" onClick={onClose}><X size={12}/></button>
        </div>
        <div className="modal-body">
          <p style={{ color: 'var(--text-2)', marginBottom: 8 }}>
            Are you sure you want to delete <strong style={{ color: 'var(--cyan)' }}>{site.name}</strong>?
          </p>
          <p style={{ color: 'var(--muted)', fontSize: 13 }}>
            This will unassign all DIDs from this site. The DIDs themselves will not be affected.
          </p>
        </div>
        <div className="modal-footer">
          <button className="btn btn-secondary" onClick={onClose}>Cancel</button>
          <button
            className="btn btn-sm"
            style={{ background: 'var(--err-bg)', color: 'var(--err)', border: '1px solid var(--err-btn-bg)' }}
            onClick={handleConfirm}
            disabled={deleting}
          >
            {deleting ? <RefreshCw size={12} style={{ animation: 'spin 0.8s linear infinite' }}/> : <Trash2 size={12}/>}
            Delete Site
          </button>
        </div>
      </div>
    </div>
  );
}

export default function Sites({ user, viewAsCustomer, customers = [] }) {
  const [sites,   setSites]   = useState([]);
  const [loading, setLoading] = useState(true);
  const [flash,        setFlash]        = useState(null);
  const [showCreate,   setShowCreate]   = useState(false);
  const [editTarget,   setEditTarget]   = useState(null);
  const [deleteTarget, setDeleteTarget] = useState(null);

  const flashMsg = (type, text) => {
    setFlash({ type, text });
    setTimeout(() => setFlash(null), 4000);
  };

  const load = useCallback(async () => {
    setLoading(true);
    try {
      const data = await listSites();
      setSites(data.sites || []);
    } catch (e) {
      flashMsg('error', `Failed to load sites: ${e.message}`);
    } finally {
      setLoading(false);
    }
  }, []);

  useEffect(() => { load(); }, [load]);

  const handleCreate = async (name, customerId) => {
    const data = await createSite(name, customerId);
    setSites(prev => [...prev, { ...data, did_count: data.did_count ?? 0 }].sort((a, b) => a.name.localeCompare(b.name)));
    setShowCreate(false);
    flashMsg('success', `Site "${name}" created`);
  };

  const handleEdit = async (name, customerId, vrpName, alertOnBusy) => {
    await updateSite(editTarget.id, name, customerId, vrpName, alertOnBusy);
    const customerName = customerId ? (customers.find(c => c.id === customerId)?.name ?? null) : null;
    setSites(prev => prev.map(s => s.id === editTarget.id
      ? { ...s, name, customer_id: customerId ?? null, customer_name: customerName, vrp_name: vrpName ?? null, alert_on_busy: alertOnBusy ? 1 : 0 }
      : s
    ));
    setEditTarget(null);
    flashMsg('success', `Site "${name}" updated`);
  };

  const handleDelete = async () => {
    await deleteSite(deleteTarget.id);
    setSites(prev => prev.filter(s => s.id !== deleteTarget.id));
    setDeleteTarget(null);
    flashMsg('success', `Site "${deleteTarget.name}" deleted`);
  };

  const totalDids    = sites.reduce((acc, s) => acc + (s.did_count || 0), 0);

  const { sortKey: sSortKey, sortDir: sSortDir, handleSort: sHandleSort, sortedRows: sortedSites } = useSortable(sites, {
    did_count:    s => s.did_count ?? 0,
    alertOnBusy:  s => s.alertOnBusy ? 1 : 0,
    customer:     s => s.customer_name || '',
  });

  return (
    <div className="content">
      {/* Customer view banner */}
      {viewAsCustomer && (
        <div className="alert alert-info" style={{ marginBottom: 16, borderColor: '#06b6d444', background: 'var(--ok-bg)' }}>
          <MapPin size={14} style={{ flexShrink: 0, color: 'var(--cyan)' }}/>
          <span style={{ color: 'var(--cyan)' }}>
            Viewing sites for <strong style={{ color: '#22d3ee' }}>{viewAsCustomer.name}</strong>
          </span>
        </div>
      )}

      <Flash msg={flash} onDismiss={() => setFlash(null)} />

      {/* Stats row */}
      <div className="stats-grid" style={{ gridTemplateColumns: 'repeat(3,1fr)', marginBottom: 20 }}>
        {[
          { label: 'Total Sites',        value: sites.length,  color: 'var(--cyan)' },
          { label: 'DIDs Assigned',      value: totalDids,     color: 'var(--ok)' },
          { label: 'Sites with DIDs',    value: sites.filter(s => s.did_count > 0).length, color: 'var(--purple)' },
        ].map(s => (
          <div key={s.label} className="stat-card">
            <div className="stat-label">{s.label}</div>
            <div className="stat-value" style={{ color: s.color }}>{s.value}</div>
          </div>
        ))}
      </div>

      <div className="card">
        <div className="card-header">
          <div>
            <div className="card-title">Sites</div>
            <div className="card-sub">
              {sites.length} site{sites.length !== 1 ? 's' : ''} — organise your DIDs by location or department
            </div>
          </div>
          <div style={{ display: 'flex', gap: 8, alignItems: 'center' }}>
            <button className="btn btn-secondary btn-sm" onClick={load} disabled={loading}>
              <RefreshCw size={13} style={loading ? { animation: 'spin 0.8s linear infinite' } : undefined}/>
              {loading ? 'Loading…' : 'Refresh'}
            </button>
            <button className="btn btn-primary btn-sm" onClick={() => setShowCreate(true)}>
              <Plus size={13}/> New Site
            </button>
          </div>
        </div>

        {loading ? (
          <div style={{ display: 'flex', alignItems: 'center', justifyContent: 'center', padding: 48, gap: 12 }}>
            <RefreshCw size={16} style={{ animation: 'spin 0.8s linear infinite', color: 'var(--cyan)' }}/>
            <span style={{ color: 'var(--muted-3)' }}>Loading sites…</span>
          </div>
        ) : sites.length === 0 ? (
          <div style={{ display: 'flex', flexDirection: 'column', alignItems: 'center', padding: 56, gap: 12 }}>
            <MapPin size={40} color="var(--border)"/>
            <div style={{ color: 'var(--muted-2)', fontSize: 14 }}>No sites yet</div>
            <div style={{ color: 'var(--muted-3)', fontSize: 13 }}>Create a site to organise your DIDs by location or department</div>
            <button className="btn btn-primary btn-sm" onClick={() => setShowCreate(true)}>
              <Plus size={13}/> Create First Site
            </button>
          </div>
        ) : (
          <div className="table-wrap">
            <table>
              <thead>
                <tr>
                  <SortTh label="Site Name"            sortKey="name"        currentKey={sSortKey} currentDir={sSortDir} onSort={sHandleSort} />
                  {user?.isAdmin && <SortTh label="Customer"    sortKey="customer"    currentKey={sSortKey} currentDir={sSortDir} onSort={sHandleSort} />}
                  <SortTh label="Voice Routing Policy" sortKey="vrpName"     currentKey={sSortKey} currentDir={sSortDir} onSort={sHandleSort} />
                  <SortTh label="DIDs Assigned"        sortKey="did_count"   currentKey={sSortKey} currentDir={sSortDir} onSort={sHandleSort} />
                  <SortTh label="Busy Alerts"          sortKey="alertOnBusy" currentKey={sSortKey} currentDir={sSortDir} onSort={sHandleSort} />
                  <SortTh label="Created"              sortKey="created_at"  currentKey={sSortKey} currentDir={sSortDir} onSort={sHandleSort} />
                  <th>Actions</th>
                </tr>
              </thead>
              <tbody>
                {sortedSites.map(s => (
                  <tr key={s.id}>
                    <td>
                      <div style={{ display: 'flex', alignItems: 'center', gap: 8 }}>
                        <div style={{
                          width: 28, height: 28, borderRadius: 6,
                          background: 'var(--ok-bg)', border: '1px solid var(--cyan)',
                          display: 'flex', alignItems: 'center', justifyContent: 'center',
                          flexShrink: 0,
                        }}>
                          <MapPin size={13} color="var(--cyan)"/>
                        </div>
                        <span style={{ fontWeight: 500, color: 'var(--text-2)' }}>{s.name}</span>
                      </div>
                    </td>
                    {user?.isAdmin && (
                      <td>
                        {s.customer_name
                          ? <span style={{ fontSize: 12, color: 'var(--info)' }}>{s.customer_name}</span>
                          : <span style={{ fontSize: 12, color: 'var(--muted-3)' }}>—</span>
                        }
                      </td>
                    )}
                    <td>
                      {s.vrp_name
                        ? <span style={{ fontSize: 11, fontFamily: 'monospace', color: 'var(--purple)' }}>{s.vrp_name}</span>
                        : <span style={{ fontSize: 12, color: 'var(--muted-3)' }}>—</span>
                      }
                    </td>
                    <td>
                      {s.did_count > 0
                        ? (
                          <span style={{
                            display: 'inline-flex', alignItems: 'center', gap: 4,
                            padding: '2px 10px', borderRadius: 12, fontSize: 12, fontWeight: 600,
                            background: 'var(--ok-bg)', color: 'var(--cyan)', border: '1px solid #0891b255',
                          }}>
                            {s.did_count} DID{s.did_count !== 1 ? 's' : ''}
                          </span>
                        )
                        : <span style={{ fontSize: 12, color: 'var(--muted-3)' }}>—</span>
                      }
                    </td>
                    <td>
                      {s.alert_on_busy
                        ? <span className="badge badge-green">On</span>
                        : <span className="badge badge-gray">Off</span>}
                    </td>
                    <td style={{ color: 'var(--muted-3)', fontSize: 12, fontFamily: 'monospace' }}>
                      {s.created_at ? s.created_at.slice(0, 10) : '—'}
                    </td>
                    <td>
                      <div style={{ display: 'flex', gap: 6 }}>
                        <button className="btn btn-secondary btn-xs" onClick={() => setEditTarget(s)}>
                          <Edit2 size={11}/> Edit
                        </button>
                        <button
                          className="btn btn-xs"
                          style={{ background: 'var(--err-bg)', color: 'var(--err)', border: '1px solid var(--err-bg)' }}
                          onClick={() => setDeleteTarget(s)}
                        >
                          <Trash2 size={11}/> Delete
                        </button>
                      </div>
                    </td>
                  </tr>
                ))}
              </tbody>
            </table>
          </div>
        )}
      </div>

      {showCreate  && <SiteModal onClose={() => setShowCreate(false)}  onSave={handleCreate} isAdmin={user?.isAdmin} customers={customers} />}
      {editTarget  && <SiteModal site={editTarget} onClose={() => setEditTarget(null)}    onSave={handleEdit}   isAdmin={user?.isAdmin} customers={customers} />}
      {deleteTarget && <DeleteModal site={deleteTarget} onClose={() => setDeleteTarget(null)} onConfirm={handleDelete} />}
    </div>
  );
}
